export interface WordPressPost {
  id: number;
  slug: string;
  title: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  date: string;
  modified: string;
  author: number;
  featured_media: number;
  categories: number[];
  tags: number[];
  _links: {
    self: Array<{ href: string }>;
    author: Array<{ href: string }>;
    'wp:featuredmedia': Array<{ href: string }>;
    'wp:attachment': Array<{ href: string }>;
    'wp:term': Array<{ taxonomy: string; href: string }>;
  };
}

export interface WordPressCategory {
  id: number;
  name: string;
  slug: string;
  description: string;
  count: number;
}

export interface WordPressAuthor {
  id: number;
  name: string;
  description: string;
  slug: string;
  avatar_urls: {
    [key: string]: string;
  };
}

export interface WordPressMedia {
  id: number;
  source_url: string;
  alt_text: string;
  media_details: {
    width: number;
    height: number;
    sizes: {
      [key: string]: {
        source_url: string;
        width: number;
        height: number;
      };
    };
  };
}

export interface WordPressComment {
  id: number;
  author_name: string;
  author_email: string;
  date: string;
  content: {
    rendered: string;
  };
  parent: number;
  post: number;
}

const BASE_URL = 'https://brianf275.sg-host.com/wp-json/wp/v2';

class BlogService {
  async fetchPosts(params: {
    page?: number;
    per_page?: number;
    search?: string;
    categories?: string;
    orderby?: string;
    order?: 'asc' | 'desc';
    bust_cache?: boolean;
  } = {}): Promise<{ posts: WordPressPost[]; totalPages: number; total: number }> {
    const searchParams = new URLSearchParams();
    
    // Add cache busting timestamp if requested
    if (params.bust_cache) {
      searchParams.append('_t', Date.now().toString());
    }
    
    // Add status=publish to ensure we get published posts
    searchParams.append('status', 'publish');
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && key !== 'bust_cache') {
        searchParams.append(key, value.toString());
      }
    });

    console.log('🔄 Fetching WordPress posts:', `${BASE_URL}/posts?${searchParams}`);
    const fetchTime = new Date().toISOString();

    try {
      const response = await fetch(`${BASE_URL}/posts?${searchParams}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        console.error('❌ WordPress API error:', response.status, response.statusText);
        throw new Error(`Failed to fetch blog posts: ${response.statusText}`);
      }

      const posts = await response.json();
      const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1');
      const total = parseInt(response.headers.get('X-WP-Total') || '0');

      console.log('✅ WordPress posts fetched:', {
        fetchTime,
        postsCount: posts.length,
        totalPages,
        total,
        latestPost: posts[0] ? {
          title: posts[0].title.rendered,
          date: posts[0].date,
          modified: posts[0].modified
        } : null
      });

      return { posts, totalPages, total };
    } catch (error) {
      console.error('❌ WordPress API fetch error:', error);
      throw new Error(`Failed to fetch blog posts: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async fetchPost(slug: string, bustCache = false): Promise<WordPressPost> {
    const searchParams = new URLSearchParams();
    searchParams.append('slug', slug);
    searchParams.append('status', 'publish');
    
    if (bustCache) {
      searchParams.append('_t', Date.now().toString());
    }

    const response = await fetch(`${BASE_URL}/posts?${searchParams}`, {
      headers: {
        'Accept': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch blog post: ${response.statusText}`);
    }

    const posts = await response.json();
    
    if (!posts.length) {
      throw new Error('Blog post not found');
    }

    return posts[0];
  }

  async fetchCategories(bustCache = false): Promise<WordPressCategory[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('per_page', '100');
    
    if (bustCache) {
      searchParams.append('_t', Date.now().toString());
    }

    const response = await fetch(`${BASE_URL}/categories?${searchParams}`, {
      headers: {
        'Accept': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch blog categories: ${response.statusText}`);
    }

    return response.json();
  }

  async fetchAuthor(id: number): Promise<WordPressAuthor> {
    const response = await fetch(`${BASE_URL}/users/${id}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch author: ${response.statusText}`);
    }

    return response.json();
  }

  async fetchMedia(id: number): Promise<WordPressMedia> {
    const response = await fetch(`${BASE_URL}/media/${id}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch media: ${response.statusText}`);
    }

    return response.json();
  }

  // Helper method to get featured image URL
  async getFeaturedImageUrl(post: WordPressPost): Promise<string | null> {
    if (!post.featured_media) return null;
    
    try {
      const media = await this.fetchMedia(post.featured_media);
      return media.source_url;
    } catch (error) {
      console.warn('Failed to fetch featured image:', error);
      return null;
    }
  }

  async fetchComments(postId: number): Promise<WordPressComment[]> {
    const response = await fetch(`${BASE_URL}/comments?post=${postId}&per_page=100`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch comments: ${response.statusText}`);
    }

    return response.json();
  }

  // Helper method to strip HTML from excerpt
  stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}

export const blogService = new BlogService();
